/**
 * Google Analytics 4 (GA4) analytics service.
 *
 * All tracking calls go through this module so event names and parameters
 * stay consistent. Every function no-ops gracefully when:
 *  - gtag has not been loaded (dev / test environments)
 *  - REACT_APP_GA_MEASUREMENT_ID is not configured
 */

const MEASUREMENT_ID = process.env.REACT_APP_FIREBASE_MEASUREMENT_ID;

const gtag = (...args) => {
    if (typeof window.gtag === 'function' && MEASUREMENT_ID) {
        window.gtag(...args);
    }
};

// ---------------------------------------------------------------------------
// Page views
// ---------------------------------------------------------------------------

export const logPageView = (pagePath, pageTitle) => {
    gtag('event', 'page_view', {
        page_path: pagePath,
        page_title: pageTitle,
    });
};

// ---------------------------------------------------------------------------
// Authentication
// ---------------------------------------------------------------------------

/** method: 'email' | 'google' */
export const logLogin = (method) => {
    gtag('event', 'user_login', { method });
};

export const logRegister = (method = 'email') => {
    gtag('event', 'user_registered', { method });
};

export const logLogout = () => {
    gtag('event', 'user_logout');
};

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------

/** priority: 'high' | 'medium' | 'low'  |  has_goal: boolean */
export const logTaskCreated = ({ priority, status, has_goal }) => {
    gtag('event', 'task_created', { priority, status, has_goal });
};

export const logTaskEdited = ({ priority, has_goal }) => {
    gtag('event', 'task_edited', { priority, has_goal });
};

/** priority: string  |  has_goal: boolean */
export const logTaskCompleted = ({ priority, has_goal }) => {
    gtag('event', 'task_completed', { priority, has_goal });
};

export const logTaskDeleted = ({ priority }) => {
    gtag('event', 'task_deleted', { priority });
};

// ---------------------------------------------------------------------------
// Goals
// ---------------------------------------------------------------------------

/** period: 'yearly' | 'quarterly' | 'monthly' */
export const logGoalCreated = ({ period, year }) => {
    gtag('event', 'goal_created', { period, year });
};

export const logGoalEdited = ({ period }) => {
    gtag('event', 'goal_edited', { period });
};

export const logGoalDeleted = ({ period }) => {
    gtag('event', 'goal_deleted', { period });
};

export const logFinishModeActivated = ({ goal_period }) => {
    gtag('event', 'finish_mode_activated', { goal_period });
};

// ---------------------------------------------------------------------------
// Calendars
// ---------------------------------------------------------------------------

export const logCalendarCreated = ({ color }) => {
    gtag('event', 'calendar_created', { color });
};

export const logCalendarEdited = () => {
    gtag('event', 'calendar_edited');
};

export const logCalendarDeleted = () => {
    gtag('event', 'calendar_deleted');
};

export const logCalendarShared = () => {
    gtag('event', 'calendar_shared');
};

export const logCalendarJoined = () => {
    gtag('event', 'calendar_joined');
};

export const logCalendarSwitched = () => {
    gtag('event', 'calendar_switched');
};

// ---------------------------------------------------------------------------
// App preferences & engagement
// ---------------------------------------------------------------------------

/** theme: 'light' | 'dark' */
export const logThemeChanged = (theme) => {
    gtag('event', 'theme_changed', { theme });
};

export const logPwaInstallPrompted = () => {
    gtag('event', 'pwa_install_prompted');
};

/** outcome: 'accepted' | 'dismissed' */
export const logPwaInstallOutcome = (outcome) => {
    gtag('event', 'pwa_install_outcome', { outcome });
};

export const logNotificationBellOpened = () => {
    gtag('event', 'notification_bell_opened');
};

export const logSearchFilterUsed = ({ search_term, owner_filter }) => {
    gtag('event', 'search_filter_used', { search_term, owner_filter });
};
